import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { money, dateShort } from "@/lib/format";
import AreaFilter from "@/components/AreaFilter";
import BillViewer from "@/components/BillViewer";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Loader2, ArrowUpDown, FileWarning, CheckCircle2, Receipt, Search, ChevronDown, Store } from "lucide-react";
const COLS = [
  { key: "date", label: "Date" },
  { key: "garage_name", label: "Garage" },
  { key: "amount", label: "Amount", num: true },
  { key: "commission", label: "Commission", num: true },
  { key: "invoice_number", label: "Invoice #" },
  { key: "entered_by", label: "By" },
  { key: "has_bill", label: "Bill" },
];

export default function OrdersPanel({ onOpenDetail }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [areaSel, setAreaSel] = useState([]);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [sort, setSort] = useState({ key: "date", dir: "desc" });
  const [missingInvoice, setMissingInvoice] = useState(false);
  const [missingBill, setMissingBill] = useState(false);
  const [garageSel, setGarageSel] = useState(""); // garage_id, "" = all
  const [garageQ, setGarageQ] = useState("");
  const fileRef = useRef(null);
  const [billTarget, setBillTarget] = useState(null);
  const [billView, setBillView] = useState(null);
  const pickBill = (o) => { setBillTarget(o); setTimeout(() => fileRef.current?.click(), 0); };
  const onFileChosen = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f || !billTarget) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post(`/garages/${billTarget.garage_id}/purchases/${billTarget.id}/bill`, fd);
      toast.success("Bill attached");
      setOrders((os) => os.map((x) => (x.id === billTarget.id ? { ...x, has_bill: true, invoice_file: data.invoice_file } : x)));
    } catch { toast.error("Upload failed"); }
  };
  const BillCell = ({ o }) => (
    <button onClick={(e) => { e.stopPropagation(); o.has_bill ? setBillView(o.invoice_file) : pickBill(o); }} data-testid={`bill-cell-${o.id}`}
      className={`inline-flex items-center gap-1 min-h-[44px] px-2.5 rounded-md text-xs font-black border ${o.has_bill ? "text-[#047857] border-[#6EE7B7] bg-[#ECFDF5]" : "text-[#B45309] border-[#FCD34D] bg-[#FFFBEB]"}`}>
      {o.has_bill ? <><CheckCircle2 className="w-3.5 h-3.5" /> View bill</> : <><FileWarning className="w-3.5 h-3.5" /> Upload</>}
    </button>
  );

  useEffect(() => {
    api.get("/purchases").then((r) => setOrders(r.data)).catch(() => toast.error("Could not load orders")).finally(() => setLoading(false));
  }, []);

  const noInvoiceCount = useMemo(() => orders.filter((o) => !o.invoice_number).length, [orders]);
  const noBillCount = useMemo(() => orders.filter((o) => !o.has_bill).length, [orders]);
  // distinct garages present in the orders, for the garage dropdown
  const garageList = useMemo(() => {
    const m = new Map();
    for (const o of orders) if (o.garage_id) m.set(o.garage_id, o.garage_name);
    return [...m.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [orders]);
  const garageName = garageList.find((g) => g.id === garageSel)?.name;

  const rows = useMemo(() => {
    let r = [...orders];
    if (areaSel.length) r = r.filter((o) => areaSel.includes(o.area));
    if (garageSel) r = r.filter((o) => o.garage_id === garageSel);
    if (from) r = r.filter((o) => (o.date || "") >= from);
    if (to) r = r.filter((o) => (o.date || "") <= to + "T23:59:59");
    if (missingInvoice) r = r.filter((o) => !o.invoice_number);
    if (missingBill) r = r.filter((o) => !o.has_bill);
    const { key, dir } = sort;
    const mul = dir === "asc" ? 1 : -1;
    r.sort((a, b) => {
      if (key === "amount" || key === "commission") return ((a[key] || 0) - (b[key] || 0)) * mul;
      if (key === "has_bill") return ((a.has_bill ? 1 : 0) - (b.has_bill ? 1 : 0)) * mul;
      return String(a[key] ?? "").localeCompare(String(b[key] ?? "")) * mul;
    });
    return r;
  }, [orders, areaSel, garageSel, from, to, missingInvoice, missingBill, sort]);

  const totals = useMemo(() => rows.reduce((a, o) => ({ n: a.n + 1, val: a.val + (o.amount || 0), com: a.com + (o.commission || 0) }), { n: 0, val: 0, com: 0 }), [rows]);

  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: (key === "date" || key === "amount" || key === "commission") ? "desc" : "asc" }));

  return (
    <div data-testid="orders-panel">
      <h1 className="text-2xl md:text-3xl font-black tracking-tight text-[#1E3A8A]">Orders</h1>
      <p className="text-sm text-[#6B7280] mt-1 mb-4">Every order across all garages, in one place.</p>

      {/* Filters */}
      <div className="bg-white border border-[#E5E7EB] rounded-xl p-3 mb-3 flex flex-wrap items-center gap-2">
        {/* Garage (searchable) */}
        <Popover>
          <PopoverTrigger asChild>
            <button data-testid="orders-garage" className="h-11 px-3 rounded-md border border-[#D1D5DB] bg-white text-sm font-medium flex items-center gap-2 min-w-[150px] justify-between">
              <span className="truncate flex items-center gap-1.5"><Store className="w-4 h-4 text-[#9CA3AF]" />{garageName || "All garages"}</span>
              <ChevronDown className="w-4 h-4 text-[#9CA3AF]" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-[min(92vw,320px)] p-0 overflow-hidden">
            <div className="p-2 border-b border-[#F3F4F6]">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                <input autoFocus value={garageQ} onChange={(e) => setGarageQ(e.target.value)} placeholder="Search garages…" data-testid="orders-garage-search"
                  className="w-full h-11 pl-9 pr-3 rounded-md border border-[#D1D5DB] text-sm focus:outline-none focus:border-[#1E3A8A]" />
              </div>
            </div>
            <div className="max-h-[42vh] overflow-y-auto p-1">
              <button onClick={() => { setGarageSel(""); setGarageQ(""); }} data-testid="orders-garage-all"
                className="w-full text-left px-3 min-h-[44px] rounded-md hover:bg-[#F9FAFB] text-sm font-bold">All garages</button>
              {garageList.filter((g) => !garageQ || g.name.toLowerCase().includes(garageQ.toLowerCase())).map((g) => (
                <button key={g.id} onClick={() => { setGarageSel(g.id); setGarageQ(""); }} data-testid={`orders-garage-opt-${g.id}`}
                  className={`w-full text-left px-3 min-h-[44px] rounded-md hover:bg-[#F9FAFB] text-sm ${garageSel === g.id ? "bg-[#EEF2FF] font-bold text-[#1E3A8A]" : ""}`}>{g.name}</button>
              ))}
              {garageList.length === 0 && <p className="text-sm text-[#9CA3AF] px-3 py-4 text-center">No garages with orders yet</p>}
            </div>
          </PopoverContent>
        </Popover>

        <AreaFilter garages={orders} selected={areaSel} onChange={setAreaSel} testId="orders-area" />
        <label className="flex items-center gap-1.5 text-sm">
          <span className="text-xs font-bold uppercase text-[#6B7280]">From</span>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="orders-from"
            className="h-11 px-2 rounded-md border border-[#D1D5DB] text-sm" />
        </label>
        <label className="flex items-center gap-1.5 text-sm">
          <span className="text-xs font-bold uppercase text-[#6B7280]">To</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="orders-to"
            className="h-11 px-2 rounded-md border border-[#D1D5DB] text-sm" />
        </label>

        {/* Two independent incomplete filters */}
        <button onClick={() => setMissingInvoice((v) => !v)} data-testid="orders-missing-invoice"
          className={`h-11 px-3 rounded-md border font-bold text-sm flex items-center gap-1.5 ${missingInvoice ? "bg-[#B45309] text-white border-[#B45309]" : "bg-white text-[#B45309] border-[#FCD34D]"}`}>
          <Receipt className="w-4 h-4" /> Missing invoice # ({noInvoiceCount})
        </button>
        <button onClick={() => setMissingBill((v) => !v)} data-testid="orders-missing-bill"
          className={`h-11 px-3 rounded-md border font-bold text-sm flex items-center gap-1.5 ${missingBill ? "bg-[#B45309] text-white border-[#B45309]" : "bg-white text-[#B45309] border-[#FCD34D]"}`}>
          <FileWarning className="w-4 h-4" /> Missing bill ({noBillCount})
        </button>

        {(areaSel.length || garageSel || from || to || missingInvoice || missingBill) ? (
          <button onClick={() => { setAreaSel([]); setGarageSel(""); setFrom(""); setTo(""); setMissingInvoice(false); setMissingBill(false); }} data-testid="orders-clear"
            className="h-11 px-3 rounded-md border border-[#D1D5DB] bg-white font-bold text-sm text-[#B91C1C]">Clear</button>
        ) : null}
      </div>

      {/* Totals — reflect current filter */}
      <div className="bg-[#1E3A8A] text-white rounded-xl px-4 py-3 mb-4 flex flex-wrap items-center gap-x-6 gap-y-1" data-testid="orders-totals">
        <span className="text-sm"><b className="text-lg">{totals.n}</b> orders</span>
        <span className="text-sm">Value <b className="text-lg">{money(totals.val)}</b></span>
        <span className="text-sm text-[#FCD34D]">Commission <b className="text-lg">{money(totals.com)}</b></span>
      </div>

      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-[#1E3A8A]" /></div>
      ) : rows.length === 0 ? (
        <div className="py-16 text-center text-[#6B7280] bg-white border border-[#E5E7EB] rounded-lg">
          <p className="font-bold text-[#111827]">No orders {orders.length ? "match these filters" : "recorded yet"}</p>
        </div>
      ) : (
        <>
          {/* Mobile cards */}
          <div className="md:hidden space-y-3" data-testid="orders-cards">
            {rows.map((o) => (
              <div key={o.id} role="button" tabIndex={0} onClick={() => onOpenDetail && onOpenDetail(o.garage_id)} data-testid={`order-row-${o.id}`}
                className="w-full text-left bg-white border border-[#E5E7EB] rounded-xl p-3 active:scale-[0.99] transition-transform cursor-pointer">
                <div className="flex items-start justify-between gap-2">
                  <span className="font-bold text-[#111827]">{o.garage_name}</span>
                  <span className="text-lg font-black text-[#047857] shrink-0">{money(o.amount)}</span>
                </div>
                <div className="text-xs text-[#6B7280] mt-0.5">{o.area || "—"} · {dateShort(o.date)}</div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs">
                  {(o.commission || 0) > 0 && <span className="font-bold text-[#B45309]">Commission {money(o.commission)}</span>}
                  <span className="text-[#6B7280]">Inv: {o.invoice_number || "—"}</span>
                  <span className="text-[#6B7280]">By {o.entered_by}</span>
                </div>
                <div className="mt-2"><BillCell o={o} /></div>
              </div>
            ))}
          </div>

          {/* Desktop table */}
          <div className="hidden md:block bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse" data-testid="orders-table">
                <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                  <tr>
                    {COLS.map((c) => (
                      <th key={c.key} onClick={() => toggleSort(c.key)} data-testid={`orders-sort-${c.key}`}
                        className={`px-3 py-2.5 text-xs font-black uppercase text-[#6B7280] cursor-pointer select-none whitespace-nowrap ${c.num ? "text-right" : "text-left"}`}>
                        <span className="inline-flex items-center gap-1">{c.label}<ArrowUpDown className={`w-3 h-3 ${sort.key === c.key ? "text-[#1E3A8A]" : "text-[#D1D5DB]"}`} /></span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((o) => (
                    <tr key={o.id} onClick={() => onOpenDetail && onOpenDetail(o.garage_id)} data-testid={`order-row-${o.id}`}
                      className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB] cursor-pointer">
                      <td className="px-3 py-2.5 text-sm text-[#374151] whitespace-nowrap">{dateShort(o.date)}</td>
                      <td className="px-3 py-2.5 text-sm font-bold text-[#111827]">{o.garage_name}<div className="text-xs font-normal text-[#9CA3AF]">{o.area}</div></td>
                      <td className="px-3 py-2.5 text-sm font-bold text-[#047857] text-right whitespace-nowrap">{money(o.amount)}</td>
                      <td className="px-3 py-2.5 text-sm font-bold text-[#B45309] text-right whitespace-nowrap">{(o.commission || 0) > 0 ? money(o.commission) : "—"}</td>
                      <td className="px-3 py-2.5 text-sm text-[#374151]">{o.invoice_number || "—"}</td>
                      <td className="px-3 py-2.5 text-sm text-[#374151]">{o.entered_by}</td>
                      <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}><BillCell o={o} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <input ref={fileRef} type="file" accept="image/*,application/pdf" hidden onChange={onFileChosen} data-testid="orders-bill-file-input" />
      <BillViewer file={billView} onClose={() => setBillView(null)} />
    </div>
  );
}
